import type { AxiosResponse } from 'axios';
import axios from 'axios';
import { keysToCamelCase, keysToSnakeCase } from '@hotjar/insights-common/lib/convert-string';
import { deleteProfileResponseDtoToModel, saveProfileResponseDtoToModel } from './mapper';
import type {
  SaveProfileResponse,
  SaveProfileResponseDto,
  ProfileDetail,
  PreviewLogoResponseDto,
  PreviewLogoResponse,
  DeleteProfileResponseDto,
  DeleteProfileResponse,
  LoginEventsResponse,
  LoginEventsResponseDto,
  UpdateProfileNotificationSettings,
  UpdateProfileNotificationSettingsResponse,
  ChangeProfilePassword,
  ChangeProfilePasswordResponse,
} from './types';

export const saveProfile = (
  userId: number,
  data: Partial<ProfileDetail>,
): Promise<SaveProfileResponse> => {
  return axios.put<SaveProfileResponseDto>(`/api/v1/profiles/${userId}`, data).then((response) => {
    return saveProfileResponseDtoToModel(response.data);
  });
};

export const getAvatarPreview = (
  userId: number,
  base64Image: string,
): Promise<PreviewLogoResponse> => {
  return axios
    .post<PreviewLogoResponseDto>(`/api/v1/profiles/${userId}`, {
      action: 'preview_logo',
      image: base64Image,
    })
    .then((response) => {
      return response.data;
    });
};

export const deleteProfile = (
  userId: number,
  email?: string,
  password?: string,
): Promise<DeleteProfileResponse> => {
  return axios
    .post<DeleteProfileResponseDto>(`/api/v1/users/${userId}`, {
      action: 'delete_user',
      password,
      email,
    })
    .then((response) => {
      return deleteProfileResponseDtoToModel(response.data);
    });
};

export const getLoginEvents = (): Promise<LoginEventsResponse> => {
  return axios.get<LoginEventsResponseDto>('/api/hercule/v1/me/events/login').then((response) => {
    // MBA: I need to manually cast it to LoginEventsResponse
    // since `keysToCamelCase` doesn't consider symbols removed from the string
    // so the type doesn't match for "@timestamp" key.
    return keysToCamelCase(response.data, true) as unknown as LoginEventsResponse;
  });
};

export const updateProfileNotificationSettings = (
  userId: number,
  settings: UpdateProfileNotificationSettings,
): Promise<AxiosResponse<UpdateProfileNotificationSettingsResponse>> => {
  return axios.put<UpdateProfileNotificationSettingsResponse>(
    `/api/v1/profiles/${userId}/notifications`,
    keysToSnakeCase(settings),
  );
};

export const changeProfilePassword = (
  userId: number,
  settings: ChangeProfilePassword,
): Promise<AxiosResponse<ChangeProfilePasswordResponse>> => {
  return axios.put<ChangeProfilePasswordResponse>(
    `/api/v1/profiles/${userId}/password`,
    keysToSnakeCase(settings),
  );
};
